import { useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;
const allTypes = Array.from(new Set(news.map((n) => n.type)));

export default function NewsPage() {
  useSeo({
    title: "天海翼最新资讯与动态 — 天海翼影视内容站",
    description:
      "天海翼最新资讯与动态，汇集新闻、深度采访与活动现场报道，按时间与资讯类型筛选，第一时间了解天海翼的相关消息。",
    keywords: "天海翼资讯,天海翼动态,天海翼新闻,天海翼采访,影视资讯,明星动态",
  });

  const [type, setType] = useState("全部");
  const [sort, setSort] = useState<"new" | "old">("new");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const list = news.filter((n) => type === "全部" || n.type === type);
    return list.sort((a, b) =>
      sort === "new" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [type, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const chipClass = (active: boolean) =>
    `cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
    }`;

  const resetFilters = () => {
    setType("全部");
    setSort("new");
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="最新资讯与动态"
        description="汇集天海翼的最新新闻、深度采访与活动现场报道，按类型与时间筛选，掌握第一手资讯。"
        crumbs={[{ label: "首页", path: "/" }, { label: "明星资讯" }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">
            共 <span className="text-primary-700">{filtered.length}</span> 条资讯
          </h2>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 md:hidden"
          >
            <i className="ri-filter-3-line" />
            {showFilters ? "收起筛选" : "筛选条件"}
          </button>
        </div>

        <div className={`${showFilters ? "block" : "hidden"} mb-8 rounded-lg border border-background-200 bg-background-100 p-5 md:mb-10 md:block`}>
          <div className="flex flex-col gap-5">
            <div>
              <p className="mb-3 text-xs font-medium text-foreground-500">资讯类型</p>
              <div className="flex flex-wrap gap-2">
                {["全部", ...allTypes].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setType(t);
                      setVisible(PAGE_SIZE);
                    }}
                    className={chipClass(type === t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-3 text-xs font-medium text-foreground-500">时间排序</p>
              <div className="flex w-full max-w-xs items-center gap-1 rounded-md border border-background-300 bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setSort("new")}
                  className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === "new" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                  }`}
                >
                  最新优先
                </button>
                <button
                  type="button"
                  onClick={() => setSort("old")}
                  className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === "old" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                  }`}
                >
                  最早优先
                </button>
              </div>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="没有找到符合条件的资讯"
            description="试试切换其他资讯类型，或重置后浏览全部动态。"
            actionText="重置筛选条件"
            onAction={resetFilters}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((item, index) => (
                <NewsCard key={item.id} item={item} index={index} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-8 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  加载更多资讯
                  <i className="ri-arrow-down-line ml-1 align-middle" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}